import { ListItem, ListItemButton, ListItemIcon, ListItemText, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';

interface NavigationItemProps {
    title: string;
    path: string;
    icon: React.ReactNode;
    onClick: () => void;
    forceActive?: boolean;
    activeColor?: string;
}

/**
 * Component representing a navigation item.
 *
 * @param {NavigationItemProps} props - The props for the navigation item.
 * @returns {JSX.Element} The rendered navigation item.
 */
export function NavigationItem(props: NavigationItemProps) {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';

    return (
        <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
                component={NavLink}
                to={props.path}
                onClick={props.onClick}
                className={props.forceActive ? 'active' : undefined}
                sx={{
                    minHeight: 20,
                    borderRadius: 2,
                    px: 2,
                    py: 1.5,
                    color: isDarkMode ? '#b0b0b0' : '#333333',
                    '&.active': {
                        backgroundColor:
                            props.forceActive && props.activeColor
                                ? alpha(props.activeColor, 0.2)
                                : isDarkMode
                                  ? alpha(theme.palette.primary.light, 0.15)
                                  : alpha(theme.palette.primary.main, 0.12),
                        color:
                            props.forceActive && props.activeColor
                                ? props.activeColor
                                : isDarkMode
                                  ? theme.palette.primary.light
                                  : theme.palette.primary.main,
                        '& .MuiListItemIcon-root': {
                            color:
                                props.forceActive && props.activeColor
                                    ? props.activeColor
                                    : isDarkMode
                                      ? theme.palette.primary.light
                                      : theme.palette.primary.dark,
                        },
                        '& .MuiListItemText-primary': {
                            fontWeight: 500,
                            color:
                                props.forceActive && props.activeColor
                                    ? props.activeColor
                                    : isDarkMode
                                      ? theme.palette.primary.light
                                      : theme.palette.primary.dark,
                        },
                    },
                    '&:hover': {
                        backgroundColor: isDarkMode
                            ? alpha(theme.palette.primary.light, 0.08)
                            : alpha(theme.palette.primary.main, 0.08),
                        color: isDarkMode ? theme.palette.primary.light : theme.palette.primary.main,
                        '& .MuiListItemIcon-root': {
                            color: isDarkMode ? theme.palette.primary.light : theme.palette.primary.main,
                        },
                    },
                    transition: theme.transitions.create(['background-color', 'color'], {
                        duration: theme.transitions.duration.short,
                    }),
                }}
            >
                <ListItemIcon
                    sx={{
                        minWidth: 40,
                        mr: 2,
                        justifyContent: 'center',
                        color: 'inherit',
                    }}
                    title={props.title}
                >
                    {props.icon}
                </ListItemIcon>
                <ListItemText
                    primary={props.title}
                    title={props.title}
                    sx={{
                        '& .MuiListItemText-primary': {
                            fontSize: '0.875rem',
                            fontWeight: 400,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                        },
                    }}
                />
            </ListItemButton>
        </ListItem>
    );
}
